import { ReactNode, useEffect, useRef, useState } from 'react'
import { Building2, LayoutGrid, Link2, Loader2, LogOut, Settings, ShieldCheck } from 'lucide-react'
import { APIError, api, deleteJSON } from './api'
import { takeReauthenticationReturn } from './reauthentication'
import { LoadingPage, Notice } from './ui'
import type { Session } from './appTypes'
import { Link, useLocation, useNavigate } from './navigation'

let pendingSessionTransition: Session['transition'] | undefined

// Only a 401 means signed out. A rate limit, a server error or a lost
// connection must not look like a sign-out: the person stays and can retry.
export function signedOut(error: unknown): boolean {
  return error instanceof APIError && error.status === 401
}

export function sessionProblem(error: unknown): string {
  if (error instanceof APIError && error.status === 429) return 'Too many requests were made. Wait one minute, then try again.'
  return 'We could not check your account. Try again.'
}

function SessionProblem({ message, onRetry }: { message: string; onRetry: () => void }) {
  return <main className="loading-page"><Notice tone="error">{message}</Notice><button className="button secondary compact" onClick={onRetry}>Try again</button></main>
}

export function SessionEntry() {
  const navigate = useNavigate()
  const [problem, setProblem] = useState('')
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    setProblem('')
    api<Session>('/api/v1/session')
      .then((session) => {
        pendingSessionTransition = session.transition
        navigate('/account/apps', { replace: true })
      })
      .catch((error) => signedOut(error) ? navigate('/login', { replace: true }) : setProblem(sessionProblem(error)))
  }, [navigate, attempt])
  if (problem) return <SessionProblem message={problem} onRetry={() => setAttempt((n) => n + 1)} />
  return <LoadingPage label="Checking your account" />
}

export function Mark({ label = 'AuthEM' }: { label?: string }) {
  const initial = label.trim().charAt(0).toUpperCase() || 'A'
  return <span className="mark" aria-hidden="true"><span>{initial}</span></span>
}

export function DelegationBanner({ session, className = '' }: { session: Session; className?: string }) {
  const [remaining, setRemaining] = useState(() => Math.max(0, Date.parse(session.expires_at) - Date.now()))
  const expiryHandled = useRef(false)
  useEffect(() => {
    if (!session.delegation) return
    const update = () => {
      const next = Math.max(0, Date.parse(session.expires_at) - Date.now())
      setRemaining(next)
      if (next === 0 && !expiryHandled.current) {
        expiryHandled.current = true
        // Reload the account route with the expired cookie intact. Its first
        // session read performs the one-time actor return and retains the
        // transition result for the visible account shell.
        window.location.assign('/account/apps')
      }
    }
    update()
    const timer = window.setInterval(update, 15_000)
    return () => window.clearInterval(timer)
  }, [session.delegation, session.expires_at])
  if (!session.delegation) return null
  return <aside className={`delegation-banner ${className}`.trim()} aria-label="Delegated company access" aria-live="polite">
    <div><strong>{session.delegation.target_company_name} · {session.delegation.target_email}</strong><span>Authenticated through {session.delegation.actor_email} · {delegationTimeLabel(remaining)}</span></div>
  </aside>
}

function delegationTimeLabel(remaining: number): string {
  if (remaining <= 0) return 'Access ended'
  const minutes = Math.ceil(remaining / 60_000)
  return minutes === 1 ? 'Ends in 1 minute' : `Ends in ${minutes} minutes`
}

export function AccountShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [session, setSession] = useState<Session | null>(null)
  const [problem, setProblem] = useState('')
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    setProblem('')
    api<Session>('/api/v1/session').then((next) => {
      if (!next.transition && pendingSessionTransition) next.transition = pendingSessionTransition
	  pendingSessionTransition = undefined
      setSession(next)
      const returnTo = takeReauthenticationReturn(next.auth_time)
      if (returnTo && location.pathname !== returnTo) { navigate(returnTo, { replace: true }); return }
      if (location.pathname === '/company' && (!next.capabilities?.manage_company || next.delegation)) navigate('/account/apps', { replace: true })
    }).catch((error) => signedOut(error) ? navigate('/login', { replace: true }) : setProblem(sessionProblem(error)))
  }, [location.pathname, navigate, attempt])
  if (problem && !session) return <SessionProblem message={problem} onRetry={() => setAttempt((n) => n + 1)} />
  const logout = async () => { await deleteJSON('/api/v1/session'); navigate('/login') }
  return <div className="app-shell"><header><Link className="wordmark" to="/account/apps"><Mark /><span>AuthEM</span></Link><nav aria-label="Account navigation"><Link className={location.pathname.startsWith('/account/apps') ? 'active' : ''} to="/account/apps"><LayoutGrid size={17} /> Apps</Link><Link className={location.pathname.includes('/security') ? 'active' : ''} to="/account/security"><ShieldCheck size={17} /> Security</Link><Link className={location.pathname.includes('/connectors') ? 'active' : ''} to="/account/connectors"><Link2 size={17} /> Account links</Link>{!session?.delegation && session?.capabilities?.manage_company && <Link className={location.pathname === '/company' ? 'active' : ''} to="/company"><Building2 size={17} /> Company</Link>}{!session?.delegation && session?.capabilities?.system_operator_role === 'superadmin' && <Link className={location.pathname.startsWith('/system') ? 'active' : ''} to="/system"><Settings size={17} /> System</Link>}</nav><div className="account-actions">{session?.identity && <div className="account-identity" role="group" aria-label="Current account"><strong>{session.identity.display_name}</strong><span>{session.identity.company_name || 'System operator'}</span>{session.identity.email && <small>{session.identity.email}</small>}</div>}<button className="button secondary compact" onClick={logout}><LogOut size={18} /> Sign out</button></div></header>{session && <DelegationBanner session={session} />}<main className="workspace">{session ? <>{session.transition?.type === 'delegation_expired' && <div className="session-transition"><Notice tone="success">Your {session.transition.target_company_name} access period ended. You are back in {session.transition.actor_email}.</Notice></div>}{children}</> : <Loader2 className="spin centered" />}</main></div>
}
